import { useMemo } from "react";
import { Facebook, Instagram, Twitter, Youtube, Music2, Link as LinkIcon, Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Social platforms offered in the dropdown. `key` matches the stored socials object.
export const SOCIAL_OPTIONS = [
    ["facebook", "Facebook"],
    ["instagram", "Instagram"],
    ["x", "X (Twitter)"],
    ["youtube", "YouTube"],
    ["tiktok", "TikTok"],
    ["other", "Other / Link"],
];

const ICONS = { facebook: Facebook, instagram: Instagram, x: Twitter, youtube: Youtube, tiktok: Music2, other: LinkIcon };
const PLACEHOLDER = {
    facebook: "https://facebook.com/yourpage",
    instagram: "https://instagram.com/yourhandle",
    x: "https://x.com/yourhandle",
    youtube: "https://youtube.com/@yourchannel",
    tiktok: "https://tiktok.com/@yourhandle",
    other: "https://linktr.ee/… or any other link",
};

const SocialIcon = ({ type }) => {
    const Icon = ICONS[type] || LinkIcon;
    return <Icon className="h-3.5 w-3.5" />;
};

/**
 * Editable list of social links — same dropdown + "add another" pattern as the
 * contact / tip fields. `value` is an array of { type, value } rows.
 */
export default function SocialsFields({ value, onChange, testIdPrefix = "socials" }) {
    const rows = useMemo(() => (value && value.length ? value : [{ type: "facebook", value: "" }]), [value]);

    const updateRow = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const addRow = () => onChange([...rows, { type: "other", value: "" }]);
    const removeRow = (i) => onChange(rows.length === 1 ? [{ type: "facebook", value: "" }] : rows.filter((_, idx) => idx !== i));

    return (
        <div data-testid={`${testIdPrefix}-fields`} className="border border-[#2a2a2a] p-4 bg-[#0f0f0f]">
            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-3">Social profiles</div>
            <div className="space-y-2">
                {rows.map((row, i) => (
                    <div key={i} className="flex gap-2 items-stretch" data-testid={`${testIdPrefix}-row-${i}`}>
                        <div className="w-[160px] shrink-0">
                            <Select value={row.type} onValueChange={(v) => updateRow(i, { type: v })}>
                                <SelectTrigger data-testid={`${testIdPrefix}-type-${i}`} className="h-11 rounded-none bg-[#141414] border-[#2a2a2a]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                                    {SOCIAL_OPTIONS.map(([v, lbl]) => (
                                        <SelectItem key={v} value={v}>
                                            <span className="flex items-center gap-2"><SocialIcon type={v} />{lbl}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Input
                            data-testid={`${testIdPrefix}-value-${i}`}
                            value={row.value}
                            onChange={(e) => updateRow(i, { value: e.target.value })}
                            placeholder={PLACEHOLDER[row.type] || "https://…"}
                            className="h-11 rounded-none bg-[#141414] border-[#2a2a2a] flex-1"
                        />
                        <button
                            type="button"
                            data-testid={`${testIdPrefix}-remove-${i}`}
                            onClick={() => removeRow(i)}
                            className="h-11 w-11 shrink-0 border border-[#2a2a2a] text-[#666] hover:text-[#ff4500] hover:border-[#ff4500] flex items-center justify-center transition-colors"
                            aria-label="Remove social link"
                            title={rows.length === 1 ? "Clear this link" : "Remove this link"}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
            <button
                type="button"
                data-testid={`${testIdPrefix}-add`}
                onClick={addRow}
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline"
            >
                <Plus className="h-3 w-3" /> Add another link
            </button>
        </div>
    );
}

// --- conversion between the { type, value } rows and the stored socials object ---
export function socialsToRows(socials) {
    if (!socials) return [];
    const out = [];
    for (const [key] of SOCIAL_OPTIONS) {
        const v = (socials[key] || "").trim();
        if (v) out.push({ type: key, value: v });
    }
    return out;
}

export function rowsToSocials(rows) {
    const map = { facebook: "", instagram: "", x: "", youtube: "", tiktok: "", other: "" };
    for (const r of rows || []) {
        const v = (r.value || "").trim();
        if (r.type && v && r.type in map) map[r.type] = v;
    }
    return map;
}
